/**
 * Os serviços de um atendimento.
 *
 * Etapa 2b: quem precisa do CONJUNTO de serviços passa a ler as linhas —
 * caixa (total e "por serviço"), estoque (material de cada um) e pacote (qual
 * serviço ele cobre). Enquanto a cliente só consegue escolher um serviço
 * (etapa 3 ainda não chegou), toda resposta aqui é idêntica à de antes: é
 * assim que esta etapa se prova, mudando a fonte sem mudar o número.
 *
 * Quem precisa de UM rótulo continua lendo `appointment.service_id`, que
 * segue apontando para o primeiro serviço: o cartão da agenda, a ficha da
 * cliente, o Quem sumiu. Trocar esses não mudaria nada e só traria risco.
 *
 * O que sustenta tudo isso, e que vale lembrar antes de mexer:
 *
 * - `appointment.price_cents` e `end_utc` são a SOMA das linhas. Quem cria
 *   atendimento grava os dois já somados; se um dia divergirem, o caixa e a
 *   agenda passam a discordar das linhas.
 * - O pacote cobre UM serviço, como desconto do preço daquela linha -- não
 *   zerando o atendimento. Ver `amountDue`.
 * - Casar atendimento com pacote é `includesService`, nunca
 *   `a.service_id === pkg.service_id`: o serviço do pacote pode ser o segundo.
 */
import type { Appointment, AppointmentService } from '../schemas/entities';

function alive(l: AppointmentService): boolean {
  return !l.deleted_at;
}

/** Agrupa as linhas por atendimento, cada grupo na ordem que ela escolheu. */
export function groupByAppointment(lines: AppointmentService[]): Map<string, AppointmentService[]> {
  const out = new Map<string, AppointmentService[]>();
  for (const l of lines) {
    if (!alive(l)) continue;
    const list = out.get(l.appointment_id);
    if (list) list.push(l);
    else out.set(l.appointment_id, [l]);
  }
  for (const list of out.values()) list.sort((a, b) => a.position - b.position);
  return out;
}

/** As linhas de um atendimento, em ordem. */
export function servicesOf(appointmentId: string, lines: AppointmentService[]): AppointmentService[] {
  return groupByAppointment(lines).get(appointmentId) ?? [];
}

/**
 * O serviço que representa o atendimento onde só cabe um: o cartão da agenda,
 * a coluna "serviço" da ficha, o "ela costuma fazer X" do Quem sumiu.
 *
 * É a primeira linha — a primeira escolha dela. Cai em `service_id` quando
 * não há linha nenhuma, o que só acontece com dado criado por app antigo que
 * ainda não sincronizou; o servidor completa, mas o aparelho pode levar um
 * tempo para receber.
 */
export function primaryServiceId(
  appointment: Pick<Appointment, 'id' | 'service_id'>,
  lines: AppointmentService[],
): string | null {
  return servicesOf(appointment.id, lines)[0]?.service_id ?? appointment.service_id ?? null;
}

/**
 * Quanto o atendimento vale, somando as linhas.
 *
 * Cai em `price_cents` do atendimento quando não há linha, pelo mesmo motivo
 * acima — e porque zerar o caixa de alguém é pior do que mostrar o valor
 * antigo.
 */
export function totalCentsOf(
  appointment: Pick<Appointment, 'id' | 'price_cents'>,
  lines: AppointmentService[],
): number {
  const own = servicesOf(appointment.id, lines);
  if (own.length === 0) return appointment.price_cents;
  return own.reduce((sum, l) => sum + l.price_cents, 0);
}

/**
 * O atendimento inclui este serviço?
 *
 * Usado para casar atendimento com pacote. Com vários serviços, um
 * atendimento cujo SEGUNDO serviço é o do pacote também conta -- comparar com
 * `service_id` sozinho perderia esse caso.
 *
 * Sem linha, cai em `service_id`: dado de app antigo ainda não sincronizado.
 */
export function includesService(
  appointment: Pick<Appointment, 'id' | 'service_id'>,
  serviceId: string,
  lines: AppointmentService[],
): boolean {
  const own = servicesOf(appointment.id, lines);
  if (own.length === 0) return appointment.service_id === serviceId;
  return own.some((l) => l.service_id === serviceId);
}

/** A duração somada das linhas, em minutos. */
export function totalMinutesOf(appointmentId: string, lines: AppointmentService[]): number {
  return servicesOf(appointmentId, lines).reduce((sum, l) => sum + l.duration_min, 0);
}

/**
 * Reparte um valor pago entre os serviços do atendimento, na proporção do
 * preço de cada um.
 *
 * É o que o "Por serviço" do caixa precisa: uma cliente paga R$ 120 por "pé e
 * mão" mais "esmaltação", e cada serviço tem de receber a parte dele.
 *
 * Com uma linha só, tudo vai para ela — idêntico a antes.
 *
 * O resto da divisão vai para a linha mais cara, não some: repartir R$ 100
 * entre três serviços iguais dá 33,33 três vezes e sobra um centavo. Jogar
 * fora faria o "Por serviço" somar menos que o total do dia, e alguém ia
 * passar a tarde procurando o centavo.
 */
export function splitByService(
  amountCents: number,
  lines: AppointmentService[],
): Array<{ serviceId: string; cents: number }> {
  if (lines.length === 0) return [];
  if (lines.length === 1) return [{ serviceId: lines[0]!.service_id, cents: amountCents }];

  const base = lines.reduce((sum, l) => sum + l.price_cents, 0);
  if (base <= 0) {
    // Tudo de graça (cortesia, pacote): o valor pago vai para a primeira.
    return [{ serviceId: lines[0]!.service_id, cents: amountCents }];
  }

  const parts = lines.map((l) => ({
    serviceId: l.service_id,
    cents: Math.floor((amountCents * l.price_cents) / base),
    peso: l.price_cents,
  }));

  const sobra = amountCents - parts.reduce((sum, p) => sum + p.cents, 0);
  if (sobra !== 0) {
    let maior = 0;
    for (let i = 1; i < parts.length; i++) if (parts[i]!.peso > parts[maior]!.peso) maior = i;
    parts[maior]!.cents += sobra;
  }

  return parts.map(({ serviceId, cents }) => ({ serviceId, cents }));
}
